import { FormModelFieldSubForm } from '@ikegps/core-shared';
import { FormDesignerPalette } from '../form.palette';
import { FormFieldEditor } from './form.field.editor';
import { FormFieldTreeView } from './form.field.tree.view';
import { m, type MithrilVNode } from '../../../mithril';
import { SelectInputComponent, type SelectInputOption, SelectInputState } from '../../../components/input/input.select';
import { FormDesigner } from '../form.designer';
import { T, Translate } from '../../../i18n';
import { SubFormType } from '@ikegps/core-api';
import { SpanSubFormTypeEditor } from './form.field.editor.subform.span';

export class FormFieldEditorSubForm extends FormFieldEditor<FormModelFieldSubForm> {
	private subFormTypeSelectState: SelectInputState<SubFormType>;
	private typeEditor: SpanSubFormTypeEditor | null = null;

	constructor(designer: FormDesigner, field: FormModelFieldSubForm) {
		super(designer, field);

		this.subFormTypeSelectState = this.initialSubFormTypeSelectState();

		// If current type is SPAN, create the span type editor
		if (this.subFormTypeSelectState.value === SubFormType.SPAN) {
			this.typeEditor = new SpanSubFormTypeEditor(this.field, this.state);
		}

		// This handler responds to USER changes of the subform type.
		this.subFormTypeSelectState.onValue((subformTypeValue) => {
			if (subformTypeValue === SubFormType.SPAN) {
				this.field.Mutator.span.enable();
				// Destroy any existing editor before creating a new one
				this.typeEditor?.destroy();
				this.typeEditor = new SpanSubFormTypeEditor(this.field, this.state);
			} else {
				this.typeEditor?.destroy();
				this.typeEditor = null;
				this.field.Mutator.span.disable();
			}
		});
	}

	private initialSubFormTypeSelectState(): SelectInputState<SubFormType> {
		const options: SelectInputOption<SubFormType>[] = [];

		options.push({ key: 'Span', value: SubFormType.SPAN });

		const state = new SelectInputState(Translate(T.TYPE), options);
		state.placeholder = Translate(T.NONE);
		state.setValue(this.field.subFormType);

		this.state.add(state);
		state.isLabelShown = true;

		return state;
	}

	private canBeSpan(): boolean {
		const spanSubform = this.designer.form?.spanSubForm;
		const isTopLevelSubform = this.field.isRoot || false;

		return spanSubform ? spanSubform.id === this.field.id : isTopLevelSubform;
	}

	override viewTree() {
		return FormFieldTreeView.viewForm(this);
	}

	override viewInfoRW() {
		return m('div', {}, [
			this.viewSubFormTypeEditor(),
			FormDesignerPalette.view((type) => this.designer.createField(this.field, type), this.depth),
		]);
	}

	private viewSubFormTypeEditor(): MithrilVNode {
		if (!this.canBeSpan()) {
			return null;
		}
		return m('div', { style: { 'margin-bottom': '32px' } }, [
			SelectInputComponent.view(this.subFormTypeSelectState),
			...(this.typeEditor ? [this.typeEditor.view()] : []),
		]);
	}
}
